"use client";

/**
 * ============================================================
 *  AuthProvider — bersih, 100% berbasis API (tanpa localStorage DB)
 *
 *  v1 dulu menyimpan daftar user + password di localStorage →
 *  menyebabkan "data beda HP vs laptop". Sekarang:
 *   - Sumber kebenaran = API/D1 (per tenant)
 *   - localStorage HANYA menyimpan session token + cache profil sendiri
 *   - Setiap load, session divalidasi ke server
 *
 *  v2 — PER-TENANT AUTH:
 *   - Token & user cache disimpan per-tenant
 *     (karpung_session_{tenantId}, karpung_user_{tenantId})
 *   - User dari tenant A TIDAK BISA mengakses tenant B
 *   - Login/register menyertakan tenant_id ke API
 *   - Backward compat: legacy key "karpung_user" tetap di-set
 * ============================================================
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import {
  api,
  getToken,
  setToken,
  clearToken,
  detectTenant,
  getTenantToken,
  setTenantToken,
  clearTenantToken,
  getUserCacheKey,
  getLegacyUserKey,
  type AuthUser,
} from "@/lib/api";
import { useChatStore } from "@/lib/chat-store";

export interface AppUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: "CUSTOMER" | "ADMIN" | "SUPER_ADMIN";
  avatar: string;
  createdAt: string;
  emailVerified?: boolean;
}

type LoginResult = { success: boolean; error?: string; role?: AppUser["role"] };
type RegisterResult = { success: boolean; error?: string; userId?: string; email?: string };

interface AuthState {
  user: AppUser | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (name: string, email: string, phone: string, password: string) => Promise<RegisterResult>;
  logout: () => void;
  updateProfile: (data: Partial<AppUser>) => void;
}

/* ── Cache profil per-tenant ── */
const LEGACY_USER_KEY = getLegacyUserKey();

function readCache(tenantId: string): AppUser | null {
  try {
    // Coba key per-tenant dulu, fallback ke legacy
    const raw = localStorage.getItem(getUserCacheKey(tenantId))
      ?? localStorage.getItem(LEGACY_USER_KEY);
    return raw ? (JSON.parse(raw) as AppUser) : null;
  } catch {
    return null;
  }
}

function writeCache(tenantId: string, u: AppUser | null) {
  try {
    const key = getUserCacheKey(tenantId);
    if (u) {
      localStorage.setItem(key, JSON.stringify(u));
      // Backward compat: legacy key agar file lama tetap jalan
      localStorage.setItem(LEGACY_USER_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(key);
      localStorage.removeItem(LEGACY_USER_KEY);
    }
  } catch {
    /* ignore */
  }
}

function toAppUser(u: AuthUser): AppUser {
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    phone: u.phone ?? "",
    role: u.role,
    avatar: "",
    createdAt: new Date().toISOString(),
    emailVerified: true,
  };
}

const AuthContext = createContext<AuthState>({
  user: null,
  isAdmin: false,
  isAuthenticated: false,
  loading: true,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  logout: () => {},
  updateProfile: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  /* Dapatkan tenant saat ini untuk inisialisasi */
  const tenantId = detectTenant();

  const [user, setUser] = useState<AppUser | null>(() =>
    getTenantToken(tenantId) ? readCache(tenantId) : null,
  );
  const [loading, setLoading] = useState(true);

  const syncChat = useCallback((u: AppUser) => {
    const chat = useChatStore.getState();
    chat.setCurrentUserId(u.id);
    chat.setAdminMode(u.role === "ADMIN" || u.role === "SUPER_ADMIN");
    chat.setCurrentTenantId(tenantId);
    if (u.role === "CUSTOMER") chat.ensureCustomerConversation(u.id, u.name, tenantId);
  }, [tenantId]);

  /* Validasi session ke server saat mount (per-tenant) */
  useEffect(() => {
    const tid = detectTenant();
    const token = getTenantToken(tid);
    if (!token) {
      setUser(null);
      writeCache(tid, null);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    api.auth
      .session()
      .then(({ data }) => {
        const u = toAppUser(data);
        setUser(u);
        writeCache(tid, u);
        syncChat(u);
      })
      .catch(() => {
        // token invalid / kadaluarsa → bersihkan hanya tenant ini
        clearTenantToken(tid);
        writeCache(tid, null);
        setUser(null);
      })
      .finally(() => {
        clearTimeout(timer);
        setLoading(false);
      });
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [syncChat]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        const tid = detectTenant();
        const { data } = await api.auth.login(email, password);
        if (data.sessionToken) setTenantToken(tid, data.sessionToken);
        const u = toAppUser(data);
        setUser(u);
        writeCache(tid, u);
        syncChat(u);
        return { success: true, role: u.role };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Login gagal" };
      }
    },
    [syncChat],
  );

  const register = useCallback(
    async (name: string, email: string, phone: string, password: string): Promise<RegisterResult> => {
      try {
        const tid = detectTenant();
        const { data } = await api.auth.register({ name, email, phone, password });
        return { success: true, userId: data.id, email: data.email };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "Registrasi gagal" };
      }
    },
    [],
  );

  const logout = useCallback(() => {
    const tid = detectTenant();
    api.auth.logout().catch(() => {});
    clearTenantToken(tid);
    writeCache(tid, null);
    setUser(null);
  }, []);

  const updateProfile = useCallback(
    (data: Partial<AppUser>) => {
      const tid = detectTenant();
      setUser((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...data };
        writeCache(tid, next);
        return next;
      });
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin: user?.role === "ADMIN" || user?.role === "SUPER_ADMIN",
        isAuthenticated: user !== null,
        loading,
        login,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
